/**
 * Theme Store - Laumeris
 * Gère le thème sombre/clair avec auto-détection du système
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

export type Theme = 'light' | 'dark' | 'system'

export const useThemeStore = defineStore('theme', () => {
  // State
  const theme = ref<Theme>('system')
  const isDark = ref(false)

  // System preference media query
  const getSystemPreference = (): boolean => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  // Apply theme to DOM
  const applyTheme = (dark: boolean) => {
    isDark.value = dark
    if (dark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Update theme based on selection
  const updateTheme = () => {
    if (theme.value === 'system') {
      applyTheme(getSystemPreference())
    } else {
      applyTheme(theme.value === 'dark')
    }
  }

  // Set theme
  const setTheme = (newTheme: Theme) => {
    theme.value = newTheme
    localStorage.setItem('laumeris-theme', newTheme)
    updateTheme()
  }

  // Toggle between light and dark
  const toggle = () => {
    if (theme.value === 'system') {
      setTheme(isDark.value ? 'light' : 'dark')
    } else {
      setTheme(theme.value === 'dark' ? 'light' : 'dark')
    }
  }

  // Initialize theme
  const initialize = () => {
    // Load saved preference
    const saved = localStorage.getItem('laumeris-theme') as Theme | null
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      theme.value = saved
    } else {
      theme.value = 'system'
    }
    
    updateTheme()

    // Listen for system preference changes
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      mediaQuery.addEventListener('change', (e) => {
        if (theme.value === 'system') {
          applyTheme(e.matches)
        }
      })
    }
  }

  return {
    theme,
    isDark,
    setTheme,
    toggle,
    initialize
  }
})

